import React, { DragEvent, useCallback, useEffect, useRef, useState } from "react";
import { AiOutlineFileAdd } from "react-icons/ai";
import { RiFilePdfFill } from "react-icons/ri";
import { MdClose, MdTextSnippet, MdDataObject } from "react-icons/md";

import {
    BiSolidFileJson,
    BiSolidFilePdf,
    BiSolidFileTxt,
    BiSolidFile, //word
} from "react-icons/bi";

interface Props {
    files: File[];
    addFiles: (f: File[]) => void;
    removeFile: (i: number) => void;
    knowledgeCountLimit: number;
    knowledgeSizeLimit: number;
    disabled: boolean;
}

export const FileUploadField = ({ files, addFiles, removeFile, knowledgeCountLimit, knowledgeSizeLimit, disabled }: Props) => {
    const ref = useRef<HTMLInputElement>(null);
    const [drag, setDrag] = useState(false);

    const drop = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDrag(false);
            addFiles(Array.from(e.dataTransfer.files));
        },
        [addFiles]
    );

    const over = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDrag(true);
    };

    const leave = () => setDrag(false);

    const change = (e: React.ChangeEvent<HTMLInputElement>) => addFiles(Array.from(e.target.files || []));

    const size = (s: number) => (s >= 1048576 ? `${(s / 1048576).toFixed(2)} MB` : s >= 1024 ? `${(s / 1024).toFixed(2)} KB` : `${s} B`);

    const getDateString = () => {
        const today = new Date().toISOString().split('T')[0];
        return today
    };

    

    const getFileIcon = (fileName: string) => {
        const lower = fileName.toLowerCase();
        if (lower.endsWith(".txt")) {
            return <BiSolidFileTxt className="text-eq-green mr-2" />;
        }
        if (lower.endsWith(".json")) {
            return <BiSolidFileJson className="text-eq-green mr-2" />;
        }
        if (lower.endsWith(".pdf")) {
            return <BiSolidFilePdf className="text-eq-green mr-2" />;
        }
        return <BiSolidFile className="text-eq-green mr-2" />;
    };


    return (
        <div className="mt-6">
            <h4 className={`text-lg font-semibold ${disabled ? 'text-gray-500' : 'text-eq-grey'}`}>Upload Documents</h4>
            <p className={`text-sm mb-4 ${disabled ? "text-gray-400" : "text-gray-500"}`}>
                Add PDF, TXT, DOCX or JSON up files (≤{knowledgeSizeLimit} MB) your assistant will learn from.
            </p>
            <div
                className={`mt-1 flex items-center justify-center px-6 pt-5 pb-6 h-48 border-2 border-dashed rounded-md ${drag ? "border-eq-green" : "border-gray-400"}`}
                onDragOver={over}
                onDragLeave={leave}
                onDrop={drop}
            >
                <div className="space-y-1 text-center">
                    <AiOutlineFileAdd className={`mx-auto h-12 w-12 ${files.length ? "text-eq-green" : "text-gray-400"}`} />
                    {files.length ? (
                        <>
                            <p className="text-sm text-gray-600">
                                {files.length} file{files.length > 1 ? "s" : ""} selected.
                            </p>
                            <div className="flex text-sm text-gray-600 justify-center">
                                <span>Drag & drop more or</span>
                                <label className="cursor-pointer text-eq-green ml-1">
                                    <span>Browse</span>
                                    <input type="file" accept=".pdf,.txt,.json,.docx" className="sr-only" multiple ref={ref} onChange={change} disabled={disabled} />
                                </label>
                            </div>
                        </>
                    ) : (
                        <div className="flex text-sm text-gray-600 justify-center">
                            <label className="cursor-pointer text-eq-green">
                                <span>Browse files</span>
                                <input type="file" accept=".pdf,.txt,.json,.docx" className="sr-only" multiple ref={ref} onChange={change} disabled={disabled} />
                            </label>
                            <p className="pl-1">or drag & drop</p>
                        </div>
                    )}
                    <p className="text-xs text-gray-500">PDF, TXT, DOCX, or JSON up to {knowledgeSizeLimit} MB</p>
                </div>
            </div>
            <div className="mt-4 space-y-1">
                <div className="text-sm text-gray-500 text-right mb-2">
                    {files.length} / {knowledgeCountLimit}
                </div>
                {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-base text-eq-grey hover:bg-gray-100 rounded px-2 py-1.5 transition-colors">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-gray-400 text-sm shrink-0 w-6 text-center">{i + 1}.</span>
                            <span className="text-lg shrink-0">{getFileIcon(f.name)}</span>
                            <span className="text-sm font-normal truncate">{f.name}</span>

                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xs text-gray-400">{size(f.size)}</span>
                            <span className="text-xs text-gray-400">{f.lastModified}</span>
                            <button
                                type="button"
                                className="text-rose-500 text-lg hover:bg-rose-500/10 rounded p-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => removeFile(i)}
                                disabled={disabled}
                            >
                                <MdClose />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
